import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import useResizeDetector from '../../hooks/useResizeDetector';
import { StyledCell } from './styled';
const ResizableCell = ({ children, onResize }) => {
    const resizeRef = useRef(null);
    const [isMounted, setIsMounted] = useState(true);
    const [resizePayload, setResizePayload] = useState(null);
    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
        };
    }, []);
    useEffect(() => {
        if (isMounted && onResize && resizePayload) {
            onResize(resizePayload);
        }
        setResizePayload(null);
    }, [resizePayload, isMounted, onResize]);
    const safeOnResize = useCallback((payload) => {
        setResizePayload(payload);
    }, []);
    useResizeDetector({
        targetRef: resizeRef,
        onResize: safeOnResize
    });
    return React.createElement("div", { ref: resizeRef }, children);
};
const DEFAULT_ROW_HEIGHT = 37;
export default ({ children, maxHeight }) => {
    const rows = children;
    if (!Array.isArray(rows)) {
        return React.createElement(React.Fragment, null, rows);
    }
    const cellCache = useMemo(() => {
        return new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: DEFAULT_ROW_HEIGHT
        });
    }, []);
    const RowRenderer = useCallback(({ key, parent, index, style, columnIndex }) => {
        return (React.createElement(CellMeasurer, { cache: cellCache, key: key, columnIndex: columnIndex !== null && columnIndex !== void 0 ? columnIndex : 0, rowIndex: index, parent: parent }, ({ measure }) => (React.createElement(StyledCell, { style: style },
            React.createElement(ResizableCell, { onResize: measure }, rows[index])))));
    }, [cellCache, rows]);
    const calcListHeight = useCallback(() => {
        if (rows.length > 30)
            return maxHeight;
        const rowHeights = rows.map((_, index) => cellCache.getHeight(index, 0));
        const totalRowHeight = rowHeights.reduce((a, b) => a + b, 0);
        return totalRowHeight === 0 ? maxHeight : Math.min(maxHeight, totalRowHeight);
    }, [rows, cellCache, maxHeight]);
    return (React.createElement(AutoSizer, { disableHeight: true }, ({ width }) => {
        var _a;
        return (React.createElement(List, { width: width, height: calcListHeight(), deferredMeasurementCache: cellCache, rowHeight: cellCache.rowHeight, rowCount: (_a = rows.length) !== null && _a !== void 0 ? _a : 0, rowRenderer: RowRenderer }));
    }));
};
